import { useMemo, useEffect, useState, useContext } from "react";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Icon from "@mui/material/Icon";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import "../DataTable/index.css";

// Material Dashboard 2 PRO React components
import MDAutocomplete from "components/MDAutocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React examples
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CommonContext } from "context/CommonCtx";
import { ENTRIES_PER_PAGE_OPTIONS } from "utils/constants";
import { DefaultColumnFilter } from "./filters";
import { Loader } from "rsuite";

function DataTable({
  table,
  isSorted = true,
  noEndBorder = false,
  isLoading = false,
  isRowClickable = false,
  showTotalEntries = true,
  entriesPerPageOption = ENTRIES_PER_PAGE_OPTIONS,
  entriesPerPage = 10,
  handlePageSizeChange = () => {},
  totalPages = 1,
  currentPage = 0,
  handlePageChange = () => {},
  sorting = [],
  handleSortByChange = () => {},
  filters = [],
  handleFiltersChange = () => {},
  isClearAllFilter = true,
  // handleClearAllFilters,
  getData = () => {},
  loading = false,
  doNotFilter,
  rowCount = 0,
}) {
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ENTRIES_PER_PAGE_OPTIONS;

  const columns = useMemo(() => table.columns, [table]);

  const data = useMemo(() => table.rows, [table]);

  // const tableInstance = useTable(
  //   { columns, data, initialState: { pageIndex: 0 } },
  //   useGlobalFilter,
  //   useSortBy,
  //   usePagination
  // );

  const tableInstance = useTable(
    {
      columns,
      data: data || [],
      initialState: {
        pageIndex: currentPage,
        pageSize: entriesPerPage,
        sortBy: sorting,
        filters: filters,
      },
      defaultColumn: { Filter: DefaultColumnFilter },
      manualPagination: true,
      manualFilters: true,
      manualSortBy: true,
      pageCount: totalPages,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    // setGlobalFilter,
    state: { pageIndex, pageSize, sortBy, filters: columnFilters },
  } = tableInstance;

  useEffect(() => {
    handleSortByChange(sortBy);
  }, [sortBy]);

  useEffect(() => {
    handlePageSizeChange(pageSize);
  }, [pageSize]);

  useEffect(() => {
    handlePageChange(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    gotoPage(0);
    handleFiltersChange(columnFilters);
  }, [columnFilters]);

  // Sync internal page state with external currentPage prop
  useEffect(() => {
    if (pageIndex !== currentPage) {
      gotoPage(currentPage);
    }
  }, [currentPage]);

  const navigate = useNavigate();

  const { CCLoader } = useContext(CommonContext);

  const clickableRow = (row) => {
    if (isRowClickable) {
      // CCLoader();
      // navigate(`/app/roles/${row?.original?.id}`);
    }
  };

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(entriesPerPage || 10), [entriesPerPage]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => {
    setPageSize(value);
    gotoPage(0);
    handlePageSizeChange(value);
  };

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input state handle
  // const onSearchChange = useAsyncDebounce((value) => {
  //   setGlobalFilter(value || undefined);
  // }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (column?.sort) {
      if (isSorted && column?.isSorted) {
        sortedValue = column.isSortedDesc ? "desc" : "asce";
      }
      // else {
      // sortedValue = "none";
      // }
    } else {
      sortedValue = false;
    }

    // if (isSorted && column.isSorted) {
    //   sortedValue = column.isSortedDesc ? "desc" : "asce";
    // } else if (isSorted && column?.sort) {
    //   sortedValue = "none";
    // } else {
    //   sortedValue = false;
    // }

    return sortedValue;
  };

  // Setting the entries starting point
  const totalCount = typeof rowCount === "number" ? rowCount : table.count;
  const entriesStart = totalCount === 0 ? 0 : Math.min(pageIndex * pageSize + 1, totalCount);

  // Setting the entries ending point
  const entriesEnd = totalCount === 0 ? 0 : Math.min((pageIndex + 1) * pageSize, totalCount);

  const customButtonStyles = {
    width: "150px",
    backgroundColor: "transparent", // Your desired background color
    color: "var(--cc-primary)", // Your desired text color
    "&:hover": {
      backgroundColor: "darkred", // Your desired background color on hover
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "0px",
    },
  };

  // useEffect(() => {
  //   setGlobalFilter();
  // }, []);

  return (
    <>
      {entriesPerPage ? (
        <MDBox className="cc-filter_record" p={2}>
          <MDBox>
            {entriesPerPage && (
              <MDBox display="flex" alignItems="center">
                <MDAutocomplete
                  disableClearable
                  value={pageSize.toString()}
                  options={entries}
                  onChange={(event, newValue) => {
                    setEntriesPerPage(parseInt(newValue, 10));
                  }}
                  size="small"
                  sx={{ width: "5rem" }}
                  renderInput={(params) => <MDInput {...params} />}
                />
                <MDTypography variant="caption" color="secondary" sx={{ textWrap: "nowrap" }}>
                  &nbsp;&nbsp;entries per page
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDBox>
              <Button onClick={() => getData()} disabled={isLoading}>
                <RefreshIcon sx={{ marginRight: "10px" }} />
                Refresh
              </Button>
            </MDBox>
            <MDBox>
              {isClearAllFilter && !doNotFilter && (
                <Button
                  onClick={(e) => {
                    setAllFilters([]);
                    gotoPage(0);
                  }}
                >
                  <CloseIcon sx={{ marginRight: "10px" }} />
                  clear All
                </Button>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      <TableContainer sx={{ boxShadow: "none" }}>
        <Table {...getTableProps()} className={`cc_table`}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => {
              const { key: headerKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <TableRow key={headerKey || key} {...headerGroupProps}>
                  {headerGroup.headers.map((column, idx) => {
                    const { key, ...headerProps } = column.getHeaderProps(
                      column.sort === false
                        ? {}
                        : (isSorted || column.sort) && column.getSortByToggleProps()
                    );
                    return (
                      <DataTableHeadCell
                        className={`${column.sort === false ? "cc-table-cursor" : "cc-table-row"}`}
                        key={key || idx}
                        {...headerProps}
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={setSortedValue(column)}
                        sx={{ borderBottom: "0 !important" }}
                        inputType={column.filterInputType}
                        inputOptions={column.filterOptions}
                        sortable={column.sortable}
                        column={column}
                      >
                        {column.render("Header")}
                      </DataTableHeadCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </MDBox>

          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 15 }}>
                  <Loader size="lg" />
                </TableCell>
              </TableRow>
            ) : table?.rows?.length > 0 ? (
              page.map((row, key) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <TableRow
                    onClick={() => clickableRow(row)}
                    className={`table_row ${isRowClickable ? "clickable_row" : ""} `}
                    key={rowKey || key}
                    {...rowProps}
                  >
                    {row.cells.map((cell, idx) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <DataTableBodyCell
                          key={cellKey || idx}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : "left"}
                          {...cellProps}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 10 }}>
                  <MDTypography variant="h6" color="text">
                    Data not found!
                  </MDTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {table?.rows?.length > 0 ? (
          <MDBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          >
            {showTotalEntries && (
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  {totalCount === 0
                    ? "No entries found"
                    : `Showing ${entriesStart} to ${entriesEnd} of ${totalCount} entries`}
                </MDTypography>
              </MDBox>
            )}
            {pageOptions.length > 1 && (
              <MDPagination variant="gradient" color="primary">
                {canPreviousPage && (
                  <MDPagination item onClick={() => previousPage()}>
                    <Icon sx={{ fontWeight: "bold", cursor: "pointer" }}>chevron_left</Icon>
                  </MDPagination>
                )}
                {renderPagination.length > 6 ? (
                  <MDBox width="5rem" mx={1} className="roundPaginationButton">
                    <MDInput
                      inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                      value={customizedPageOptions[pageIndex]}
                      onChange={(handleInputPagination, handleInputPaginationValue)}
                    />
                  </MDBox>
                ) : (
                  renderPagination
                )}
                {canNextPage && (
                  <MDPagination item onClick={() => nextPage()}>
                    <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                  </MDPagination>
                )}
              </MDPagination>
            )}
          </MDBox>
        ) : null}
      </TableContainer>
      {/* <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination variant="gradient" color="primary">
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold", cursor: "pointer" }}>chevron_left</Icon>
              </MDPagination>
            )}  
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1} className="roundPaginationButton">
                <MDInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox> */}
    </>
  );
}

export default DataTable;
